import { useEffect, useRef, useState } from 'react';
import PublicWatchShell from '../../components/PublicWatchShell.jsx';
import PageState from '../../components/PageState.jsx';
import SectionCard from '../../components/SectionCard.jsx';
import Notice from '../../components/Notice.jsx';
import StatusWord from '../../components/StatusWord.jsx';
import WatchPreferencesForm from '../../components/WatchPreferencesForm.jsx';
import { postPublic } from '../../lib/watch-public-api.js';
import { takeFragment } from '../../lib/watch-fragment.js';

// Plain, generic wording: nothing says which check failed, and nothing offers a login.
const MESSAGES = {
  invalid: 'This link is not valid.',
  revoked: 'This link no longer works. Use the link in your most recent Watch email.',
  busy: 'Too many attempts. Try again later.',
  offline: 'The onGuard portal could not be reached. Try again later.',
  failed: 'Your preferences could not be saved. Open the link in your email again.',
};
const RETRY_MESSAGE = 'Your preferences could not be saved. Try again.';

export default function WatchPreferencesPage() {
  const token = useRef(null);
  const started = useRef(false);
  const [phase, setPhase] = useState('loading');
  const [data, setData] = useState(null);
  const [saved, setSaved] = useState(null);
  const [notice, setNotice] = useState(null);
  const [saving, setSaving] = useState(false);

  // Read the fragment once, remove it from the address bar, then view (a read).
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const frag = takeFragment(window);
    token.current = frag.p ?? null;
    if (!frag.p) { setPhase('invalid'); return; }
    postPublic('/watch/links/preferences/view', { token: frag.p })
      .then((r) => {
        if (r.status === 200 && r.body?.state === 'ready' && r.body.formNonce) { setData(r.body); setPhase('ready'); }
        else setPhase(r.status === 410 ? 'revoked' : r.status === 429 ? 'busy' : 'invalid');
      })
      .catch(() => setPhase('offline'));
  }, []);

  async function save(categories) {
    setSaving(true);
    setSaved(null);
    setNotice(null);
    try {
      const r = await postPublic('/watch/links/preferences', { token: token.current, categories, formNonce: data.formNonce });
      if (r.status === 200 && r.body?.state === 'saved') {
        // The portal answers with the categories it changed; keep the rest as they were.
        setData((d) => ({ ...d, categories: { ...d.categories, ...r.body.categories } }));
        setSaved('Saved.');
      } else if (r.status === 410) setPhase('revoked');
      else if (r.status === 429) setNotice(MESSAGES.busy);
      else if (r.status >= 500) setNotice(RETRY_MESSAGE);
      else setPhase('failed');
    } catch {
      setNotice(RETRY_MESSAGE);
    } finally {
      setSaving(false);
    }
  }

  let content;
  if (phase === 'loading') content = <PageState state="loading" cards={0} label="Opening your link" />;
  else if (phase !== 'ready') content = <Notice tone={phase === 'busy' || phase === 'offline' ? 'warn' : 'bad'} titleAs="h1" title={MESSAGES[phase] ?? MESSAGES.invalid} />;
  else {
    content = (
      <SectionCard ariaLabel="Notification preferences" bodyClassName="space-y-4">
        <h1 className="text-xl">Notification preferences for {data.tenantName}</h1>
        <p className="text-sm text-ipc-ink">{data.displayName}, choose how Watch tells you about each kind of change. Notices about your own account and about Watch itself always arrive immediately.</p>
        <WatchPreferencesForm key={JSON.stringify(data.categories)} model={data.model} categories={data.categories} onSave={save} saving={saving} />
        {saved ? <p className="text-sm" role="status"><StatusWord tone="good">{saved}</StatusWord></p> : null}
        {notice ? <p className="text-sm" role="alert"><StatusWord tone="bad" strong={false}>{notice}</StatusWord></p> : null}
      </SectionCard>
    );
  }
  return <PublicWatchShell>{content}</PublicWatchShell>;
}
